/**
 * ChannelsTestReport — результат последнего автотеста каналов.
 *
 * До 02.08 отчёт складывался в задачу и событие, но в кабинете не показывался:
 * оператор видел «Автотест каналов запущен» и шёл смотреть в базу. Теперь видно
 * по каналам — что проверено живьём, что структурно, где вебхук.
 *
 * Почему вебхук вынесен отдельно: `getMe` подтверждает только валидность токена.
 * Клиенту сообщения доставляет ВЕБХУК — на этом и горели 16.07, когда боты были
 * «живые», а вебхуки указывали на снятый n8n.
 */

import { useQuery } from '@tanstack/react-query'
import { CheckCircle2, XCircle, AlertTriangle, Loader2, Radio } from 'lucide-react'
import { channelsApi, type ChannelTestItem, type ChannelTestReport } from '@/api/client'

const USE_MOCK = import.meta.env.VITE_USE_MOCK != null
  ? import.meta.env.VITE_USE_MOCK === 'true'
  : import.meta.env.DEV

const MOCK_REPORT: ChannelTestReport = {
  ran: true,
  all_ok: false,
  finished_at: new Date().toISOString(),
  channels: [
    {
      channel: 'telegram',
      ok: true,
      detail: 'бот @newvid_bot живой, вебхук настроен',
      mode: 'live',
      warnings: [],
      webhook: { url: 'https://ops.touchbrand.ru/ops/webhook/tg/…', pending_update_count: 0 },
    },
    {
      channel: 'max',
      ok: false,
      detail: 'config.max_channel_id отсутствует',
      mode: 'structural',
      warnings: [],
    },
  ],
}

const MODE_LABEL: Record<string, string> = {
  live: 'живая проверка',
  'offline-check': 'без сети: есть токен и операторы',
  structural: 'проверен конфиг',
}

function ChannelRow({ item }: { item: ChannelTestItem }) {
  const warnings = item.warnings ?? []
  const hasWarn = warnings.length > 0
  const Icon = item.ok ? (hasWarn ? AlertTriangle : CheckCircle2) : XCircle
  const color = item.ok ? (hasWarn ? 'text-amber-500' : 'text-green-600') : 'text-red-600'

  return (
    <div className="flex items-start gap-2 px-3 py-2">
      <Icon className={`h-4 w-4 shrink-0 mt-0.5 ${color}`} />
      <div className="min-w-0 space-y-0.5">
        <p className="text-sm text-slate-800">
          {item.channel}
          <span className="ml-2 text-[11px] text-slate-400">
            {MODE_LABEL[item.mode] ?? item.mode}
          </span>
        </p>
        <p className="text-xs text-slate-600 break-words">{item.detail}</p>
        {warnings.map((w, i) => (
          <p key={i} className="text-xs text-amber-700 break-words">⚠ {w}</p>
        ))}
        {item.webhook?.url && (
          <p className="text-[11px] text-slate-400 break-all">
            вебхук: {item.webhook.url}
            {typeof item.webhook.pending_update_count === 'number' &&
              item.webhook.pending_update_count > 0 &&
              ` · в очереди ${item.webhook.pending_update_count}`}
          </p>
        )}
      </div>
    </div>
  )
}

interface Props {
  dealId: string
  /** Растёт после запуска автотеста — повод перечитать отчёт. */
  refreshKey?: number
}

export function ChannelsTestReport({ dealId, refreshKey = 0 }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ['channels-test', dealId, refreshKey],
    queryFn: async (): Promise<ChannelTestReport> => {
      if (USE_MOCK) return MOCK_REPORT
      return channelsApi.lastTest(dealId)
    },
    staleTime: 10 * 1000,
    // Воркер берёт задачу раз в 5 с — пару раз переспросим, пока отчёт не приедет.
    refetchInterval: (q) => (q.state.data?.ran ? false : 5000),
  })

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Читаю результат автотеста…
      </div>
    )
  }

  if (!data?.ran) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
        Автотест ещё не запускали. Он проверяет телеграм-бота живьём (валиден ли токен
        и куда указывает вебхук) и конфигурацию остальных каналов.
      </div>
    )
  }

  const when = data.finished_at
    ? new Date(data.finished_at).toLocaleString('ru-RU', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
      })
    : null

  return (
    <div className="rounded-md border border-slate-200 overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-slate-50">
        <div className="flex items-center gap-2">
          <Radio className="h-3.5 w-3.5 text-slate-500" />
          <span className="text-xs font-medium text-slate-700">
            Автотест каналов: {data.all_ok ? 'все отвечают' : 'есть проблемы'}
          </span>
        </div>
        {when && <span className="text-[11px] text-slate-400">{when}</span>}
      </div>
      <div className="divide-y divide-slate-100 bg-white">
        {data.channels.map((c) => (
          <ChannelRow key={c.channel} item={c} />
        ))}
      </div>
    </div>
  )
}
